import {
  FormDialogSubmitHandler,
  OutlinedInput,
  RHFCheckbox,
} from '@aibees/shared/components';
import { Member } from '@aibees/shared/definition';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useUpdateWorkspace } from '@aibees/shared/workspace';
import { Stack, Typography } from '@mui/material';
import { capitalize, random } from 'lodash-es';
import { memo } from 'react';
import { useEditMember } from '../../api/use-edit-member';
import { config, getScheme } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { ChangeMemberStatusDialogProps as Props } from './types';
import { useMemberStatusTexts } from './use-member-status-texts';

export const DeactivateMemberDialog = memo(
  ({ className, item, status, onClose }: Props) => {
    const notify = useNotify();
    const api = useEditMember(item.user?.id);
    const updateWorkspace = useUpdateWorkspace();
    const { title, titleUser, label, note1, note2, buttonTitle } =
      useMemberStatusTexts(status);

    const handleSubmit: FormDialogSubmitHandler<Member> = (data, e) => {
      e.startProgress();
      api
        .mutateAsync({
          member: { ...data, ...{ status } },
        })
        .then((workspace) => {
          updateWorkspace(workspace);
          e.toggle();
          notify.complete(
            `User ${title}`,
            `The user ${item.user?.firstName} ${
              item.user?.lastName
            } has been successfully ${title.toLowerCase()}`,
            random(1, 100),
            {
              icon: notificationIconMap.UserManagement!,
            }
          );
        })
        .catch(() => {
          e.endProgress();
        });
    };

    return (
      <Styled.Wrapper
        className={classnames('EditRoleDialog', className)}
        {...config}
        submitButtonProps={{
          children: buttonTitle,
        }}
        scheme={getScheme(status)}
        defaultOpen
        defaultValues={{ type: item.type, confirmed: false }}
        onClose={onClose}
        onSubmit={handleSubmit}
        contentSx={SX.content}
        maxWidth="xs"
        fullWidth
      >
        <Stack gap={1}>
          <OutlinedInput
            size="small"
            fullWidth
            disabled
            value={item.user?.username || ''}
            startIcon="email-unread"
            label="Email:"
          />

          <OutlinedInput
            name="type"
            autoComplete="off"
            size="small"
            disabled
            value={`${item.user?.firstName} ${
              item.user?.lastName
            } (${capitalize(item.type)})`}
            startIcon="edit-role"
            label={`${titleUser} User:`}
          />
          <Stack>
            <RHFCheckbox
              name="confirmed"
              label={`Confirm ${label} this account`}
              sx={SX.checkbox}
              required
            >
              {(hasError) => (
                <Typography
                  variant="body5"
                  fontWeight={400}
                  sx={SX.checkboxSub}
                  color={hasError ? 'error.500' : 'grey.500'}
                >
                  {`The user status will change to '${note1}', but ${note2} is possible later.`}
                </Typography>
              )}
            </RHFCheckbox>
          </Stack>
        </Stack>
      </Styled.Wrapper>
    );
  }
);

DeactivateMemberDialog.displayName = 'DeactivateMemberDialog';
